<template>
  <v-container v-if="$hasMatchingTenantRoles(['tenant_admin'])" fluid pa-0>
    <v-row no-gutters>
      <v-col cols="12">
        <v-row no-gutters>
          <v-col class="pt-md-5" cols="12" md="5">
            <div class="image-holder">
              <div class="grey lighten-5 rounded-circle" style="width: 100%; height: 100%">
                <v-slide-x-transition>
                  <v-row v-if="imageIsLoaded" align="center" class="fill-height" justify="center" no-gutters>
                    <img src="img/undraw_accept_tasks_re_09mv.svg" style="height: 90%"/>
                  </v-row>
                </v-slide-x-transition>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="7">
            <dismiss-onboarding-button/>
            <div key="0" class="text-left">
              <div class="title text-sm-h5 font-weight-bold mt-5 mb-5">
                {{ $t('onboarding.tasks') }}
              </div>
              <div class="subtitle-2 text-sm-subtitle-1">{{ $t('onboarding.tasks_info') }}</div>
              <template v-if="filteredSuggestedPersonnelTasks && filteredSuggestedPersonnelTasks.length > 0">
                <div class="subtitle text-sm-h6 font-weight-bold mt-5 mb-5">
                  {{ $t('onboarding.tasks_for_personnel') }}
                </div>
                <div>
                  <div class="tasks-ul caption text-sm-body-2">
                    <template v-for="task in filteredSuggestedPersonnelTasks">
                      <v-row :key="task.key" dense>
                        <v-col class="d-flex tasks-li align-center" cols="12">
                          <div class="mr-2">
                            <v-icon color="secondary">mdi mdi-circle-small</v-icon>
                          </div>
                          <div>
                            {{ task.nameText }}
                          </div>
                        </v-col>
                      </v-row>
                    </template>
                  </div>
                </div>
              </template>
              <template v-if="filteredSuggestedManagementTasks && filteredSuggestedManagementTasks.length > 0">
                <div class="subtitle text-sm-h6 font-weight-bold mt-5 mb-5">
                  {{ $t('onboarding.tasks_for_management') }}
                </div>
                <div>
                  <div class="tasks-ul caption text-sm-body-2">
                    <template v-for="task in filteredSuggestedManagementTasks">
                      <v-row :key="task.key" dense>
                        <v-col class="d-flex tasks-li align-center" cols="12">
                          <div class="mr-2">
                            <v-icon color="secondary">mdi mdi-circle-small</v-icon>
                          </div>
                          <div>
                            {{ task.nameText }}
                          </div>
                        </v-col>
                      </v-row>
                    </template>
                  </div>
                </div>
              </template>
              <basic-button :loading="loadingTaskSave" class="mt-10" color="primary" outlined
                            @click="saveSuggestedTasks">
                <v-icon left>mdi mdi-arrow-right</v-icon>
                {{ $t('onboarding.save_tasks_and_continue') }}
              </basic-button>
            </div>
            <div class="text-body-2 text-sm-body-1 primary--text text--darken-1 mt-10">
              {{ $t('onboarding.tasks_info2') }}
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <img src="img/undraw_accept_tasks_re_09mv.svg" style="display: none" @load="imageIsLoaded = true"/>
  </v-container>
</template>

<script>
import BasicButton from '@/components/BasicButton';
import taskApi from '@/api/task';
import staticTaskApi from '@/api/statictask';
import dayjs from 'dayjs';
import pLimit from 'p-limit';
import DismissOnboardingButton from '@/components/Tenant/Onboarding/DismissOnboardingButton';

export default {
  name: 'CreateTasks',
  components: {DismissOnboardingButton, BasicButton},
  data() {
    return {
      imageIsLoaded: false,
      loadingTaskSave: false,
      suggestedTasks: [],
      tasks: [],
    };
  },
  methods: {
    goToNextStep() {
      this.$emit('next-step');
    },
    async reloadTasks() {
      try {
        const tasks = await taskApi.getTasks();
        this.tasks = tasks;
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_loading_tasks'));
      }
    },
    async reloadSuggestedTasks() {
      try {
        let tasks = await staticTaskApi.getStaticTasks();
        tasks = tasks.map(task => {
          task.searchString = `${task.nameText} ${task.descriptionText}`;
          return task;
        });
        this.suggestedTasks = tasks;
      } catch (e) {
        //this.$handleApiError(e, this.$t('errors.error_loading_tasks'));
      }
    },
    async saveSuggestedTasks() {
      this.loadingTaskSave = true;
      try {
        const limit = pLimit(3);
        const promises = [];
        const selectedSuggestedTasks = this.filteredSuggestedPersonnelTasks.concat(this.filteredSuggestedManagementTasks);
        for (let stask of selectedSuggestedTasks) {
          promises.push(
            limit(async () => {
              try {
                const t = {
                  staticTaskId: stask.id,
                  type: stask.type,
                  name: stask.nameText,
                  description: stask.descriptionText,
                  priority: stask.priority,
                  category: null,
                  dueDate: dayjs().add(30, 'days').format('YYYY-MM-DD'),
                };
                await taskApi.createTask(t);
              } catch (e) {
                this.$handleApiError(e, this.$t('errors.error_saving_task'));
              }
            })
          );
        }
        await Promise.all(promises);

        this.$showSuccessNotification(this.$t('tasks.suggested_tasks_saved'));
        this.goToNextStep();
      } catch (e) {
        console.error(e);
        this.$handleApiError(e, this.$t('errors.error_saving_task'));
      }
      this.loadingTaskSave = false;
      // save selected tasks
    },
  },
  computed: {
    filteredSuggestedPersonnelTasks() {
      return this.suggestedTasks
        .filter(task => {
          return task?.type === 'personnel_help';
        })
        .filter(task => {
          return !this.tasks.find(t => t.staticTaskId === task.id && t.status !== 'DONE');
        });
    },
    filteredSuggestedManagementTasks() {
      return this.suggestedTasks
        .filter(task => {
          return task?.type === 'management_help';
        })
        .filter(task => {
          return !this.tasks.find(t => t.staticTaskId === task.id && t.status !== 'DONE');
        });
    },
  },
  mounted() {
    this.reloadTasks().then(() => this.reloadSuggestedTasks());
  },
};
</script>

<style lang="scss" scoped>
.image-holder {
  position: relative;
  margin: auto;
  width: 245px;
  height: 245px;
}

@media screen and (min-width: 1500px) {
  .image-holder {
    width: 285px;
    height: 285px;
  }
}
</style>
